import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Box, Text, Banner, Stack, Label, DataTable, Tooltip } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { createColumnHelper } from '@tanstack/react-table';
import type { CalendarDate } from '@internationalized/date';
import { parseDate } from '@internationalized/date';
import { getObjectTypeDisplayName, getObjectTypeOptions } from '../utils/objectTypeMapping';
import type { AuditLogResponse, AuditLogFilters, AuditLogEntry } from '../types';
import {
  UserFilter,
  IPAddressFilter,
  ObjectIdFilter,
  ObjectTypeFilter,
  DateFilter,
  AttributeNameFilter,
  ActionFilter,
} from './filters';
import { formatAttributeName, formatHumanFriendlyDate } from '../utils/formatting';
import { getChangedFieldNames } from '../utils/diffing';
import { AuditLogExpandedDetails } from './AuditLogExpandedDetails';

// FilterValue type definition
type FilterValue = {
  key: string;
  value: unknown;
  isDisabled?: boolean;
};

interface AuditLogsTableProps {
  data?: AuditLogResponse;
  isLoading?: boolean;
  error?: unknown;
  filters: AuditLogFilters;
  /**
   * Callback when filters change. Should be memoized with useCallback to prevent unnecessary re-renders.
   * @example
   * const handleFiltersChange = useCallback((newFilters) => {
   *   setFilters(prev => ({ ...prev, ...newFilters }));
   * }, []);
   */
  onFiltersChange: (filters: Partial<AuditLogFilters>) => void;
  context: 'account' | 'workspace';
  contextId: number;
  highlightedLogId?: string | null;
  allUsers: Array<{ user_id: number; email: string; deleted?: boolean }>;
  isLoadingUsers?: boolean;
  onLoadUsers?: () => void;
}

const columnHelper = createColumnHelper<AuditLogEntry>();

// Stable empty array to avoid creating new array reference on every render
const EMPTY_LOGS: AuditLogEntry[] = [];

export const AuditLogsTable: React.FC<AuditLogsTableProps> = ({
  data,
  isLoading,
  error,
  filters,
  onFiltersChange,
  context,
  contextId,
  highlightedLogId,
  allUsers,
  isLoadingUsers = false,
  onLoadUsers,
}) => {
  const [sorting, setSorting] = useState({
    id: 'timestamp',
    desc: filters.sort_order === 'desc',
  });

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    timestamp: true,
    user: true,
    ip_address: false, // IP Address hidden by default
    object_type: true,
    change_type: true,
    object_id: true,
    changes: true,
  });

  // Filter state for DataTable integrated filters
  const [filterValues, setFilterValues] = useState<FilterValue[]>(() => {
    const values: FilterValue[] = [];
    if (filters.user_id !== undefined) {
      values.push({ key: 'user_id', value: filters.user_id.toString() });
    }
    if (filters.ip_addr != null && filters.ip_addr !== '') {
      values.push({ key: 'ip_addr', value: filters.ip_addr });
    }
    if (filters.object_type != null && filters.object_type !== '') {
      values.push({ key: 'object_type', value: filters.object_type });
    }
    if (filters.object_id != null && filters.object_id !== '') {
      values.push({ key: 'object_id', value: filters.object_id });
    }
    if (filters.attribute_name != null && filters.attribute_name !== '') {
      values.push({ key: 'attribute_name', value: filters.attribute_name });
    }
    if (filters.action != null && filters.action !== '') {
      values.push({ key: 'action', value: filters.action });
    }
    if (filters.start_time != null && filters.start_time !== '') {
      values.push({ key: 'start_time', value: filters.start_time });
    }
    if (filters.end_time != null && filters.end_time !== '') {
      values.push({ key: 'end_time', value: filters.end_time });
    }
    return values;
  });

  // Date state for account context
  const [_startDate, setStartDate] = useState<CalendarDate | null>(() => {
    if (filters.start_time != null && filters.start_time !== '') {
      try {
        const dateStr = filters.start_time.split('T')[0];
        return parseDate(dateStr);
      } catch {
        return null;
      }
    }
    return null;
  });

  const [_endDate, setEndDate] = useState<CalendarDate | null>(() => {
    if (filters.end_time != null && filters.end_time !== '') {
      try {
        const dateStr = filters.end_time.split('T')[0];
        return parseDate(dateStr);
      } catch {
        return null;
      }
    }
    return null;
  });

  // Memoize logs array to prevent ExpandedRowComponent from being recreated unnecessarily
  // Only recreate when the actual data reference changes
  const memoizedLogs = useMemo(() => data?.data ?? EMPTY_LOGS, [data?.data]);
  const logs = memoizedLogs;
  const users = data?.users ?? [];
  const nextCursor = data?.pagination?.next_cursor;
  // next_cursor of 0, null, or undefined means no more data
  const hasMore = nextCursor !== undefined && nextCursor !== null && nextCursor !== 0;
  // Check if we're in deep link mode (viewing a single audit log)
  const isDeepLinkMode = highlightedLogId != null && highlightedLogId !== '';

  // Use a ref to store the current logs array so cell functions can access it
  // without requiring columns to be recreated when logs change
  const logsRef = useRef(logs);
  useEffect(() => {
    logsRef.current = logs;
  }, [logs]);

  const handleLoadMore = useCallback(async () => {
    if (hasMore === false || isLoadingMore === true || nextCursor == null) return;

    setIsLoadingMore(true);
    try {
      // Update filters with the next cursor
      onFiltersChange({ cursor: nextCursor.toString() });
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, nextCursor, onFiltersChange]);

  const getUserEmail = (userId: number) => {
    if (userId === 0) {
      return 'system@customer.io';
    }
    const user = users.find((u) => u.user_id === userId);
    return user?.email ?? 'Unknown';
  };

  // Filter handlers - memoized to maintain referential stability
  const handleFilterChange = useCallback(
    (key: string, value: string) => {
      // Calculate new filter values and build filters inside setState to avoid race condition
      setFilterValues((prev) => {
        let newValues = [...prev];

        // If object_type is changing, clear attribute_name and action
        if (key === 'object_type') {
          newValues = newValues.filter((f) => f.key !== 'attribute_name' && f.key !== 'action');
        }

        const existingIndex = newValues.findIndex((f) => f.key === key);
        if (existingIndex !== -1) {
          newValues[existingIndex] = { key, value };
        } else {
          newValues = [...newValues, { key, value }];
        }

        // Build complete filters object from updated filter values
        // Clear cursor when filter values change (to reset pagination)
        const newFilters: Partial<AuditLogFilters> = { cursor: undefined };

        // Process all filter values to build the complete filter object
        newValues.forEach((filter) => {
          const filterValue = filter.value;
          if (filterValue === '') return;

          // Type guard to ensure filterValue is a string
          const filterValueStr = typeof filterValue === 'string' ? filterValue : String(filterValue);

          switch (filter.key) {
            case 'user_id': {
              const parsedUserId = parseInt(filterValueStr, 10);
              if (!Number.isNaN(parsedUserId)) {
                newFilters.user_id = parsedUserId;
              }
              break;
            }
            case 'ip_addr':
              newFilters.ip_addr = filterValueStr;
              break;
            case 'object_type':
              newFilters.object_type = filterValueStr;
              break;
            case 'object_id':
              newFilters.object_id = filterValueStr;
              break;
            case 'attribute_name':
              newFilters.attribute_name = filterValueStr;
              break;
            case 'action':
              newFilters.action = filterValueStr;
              break;
            case 'start_time':
              newFilters.start_time = filterValueStr;
              // Update local date state
              try {
                const dateStr = filterValueStr.split('T')[0];
                setStartDate(parseDate(dateStr));
              } catch {
                setStartDate(null);
              }
              break;
            case 'end_time':
              newFilters.end_time = filterValueStr;
              // Update local date state
              try {
                const dateStr = filterValueStr.split('T')[0];
                setEndDate(parseDate(dateStr));
              } catch {
                setEndDate(null);
              }
              break;
          }
        });

        // Clear date states if filters were removed
        if (key === 'start_time' && value === '') {
          setStartDate(null);
        }
        if (key === 'end_time' && value === '') {
          setEndDate(null);
        }

        // Apply the filters
        onFiltersChange(newFilters);

        return newValues;
      });
    },
    [onFiltersChange],
  );

  const handleFilterClear = useCallback(
    (key: string) => {
      setFilterValues((prev) => prev.filter((f) => f.key !== key));

      // Clear date state if clearing date filters
      if (key === 'start_time') {
        setStartDate(null);
      } else if (key === 'end_time') {
        setEndDate(null);
      }

      // Clear cursor to reset pagination when filter is cleared
      onFiltersChange({ [key]: undefined, cursor: undefined });
    },
    [onFiltersChange],
  );

  const handleFilterClearAll = useCallback(() => {
    setFilterValues([]);
    setStartDate(null);
    setEndDate(null);
    onFiltersChange({
      user_id: undefined,
      ip_addr: undefined,
      object_type: undefined,
      object_id: undefined,
      attribute_name: undefined,
      action: undefined,
      start_time: undefined,
      end_time: undefined,
      cursor: undefined,
    });
  }, [onFiltersChange]);

  // Filter configuration for DataTable
  const auditFilters = useMemo(
    () => [
      {
        key: 'user_id',
        label: 'User',
        type: 'custom' as const,
        icon: 'person' as const,
        selectedLabel: (value: string) => {
          if (value === '0') {
            return 'User: system@customer.io';
          }
          const user = allUsers.find((u) => u.user_id.toString() === value);
          return user != null ? `User: ${user.email}` : `User ID: ${value}`;
        },
        editComponent: ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
          <UserFilter
            value={value}
            onChange={onChange}
            allUsers={allUsers}
            isLoadingUsers={isLoadingUsers}
            onLoadUsers={onLoadUsers}
          />
        ),
      },
      {
        key: 'ip_addr',
        label: 'IP Address',
        type: 'custom' as const,
        icon: 'inspect' as const,
        selectedLabel: (value: string) => `IP: ${value}`,
        editComponent: ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
          <IPAddressFilter value={value} onChange={onChange} />
        ),
      },
      {
        key: 'object_type',
        label: 'Object Type',
        type: 'custom' as const,
        icon: 'object' as const,
        selectedLabel: (value: string) => {
          const option = getObjectTypeOptions(context === 'account' ? 'account' : 'workspace').find(
            (opt) => opt.value === value,
          );
          return option?.label ?? value;
        },
        editComponent: ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
          <ObjectTypeFilter
            value={value}
            onChange={onChange}
            options={getObjectTypeOptions(context === 'account' ? 'account' : 'workspace').filter(
              (opt) => opt.value !== '',
            )}
          />
        ),
      },
      {
        key: 'object_id',
        label: 'Object ID*',
        type: 'custom' as const,
        icon: 'hashtag' as const,
        selectedLabel: (value: string) => `Object ID: ${value}`,
        editComponent: ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
          const objectTypeValue = filterValues.find((fv) => fv.key === 'object_type')?.value as string;
          return <ObjectIdFilter value={value} onChange={onChange} objectType={objectTypeValue} />;
        },
      },
      {
        key: 'attribute_name',
        label: 'Attribute Name*',
        type: 'custom' as const,
        icon: 'properties' as const,
        selectedLabel: (value: string) => `Attribute: ${value}`,
        editComponent: ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
          const objectTypeValue = filterValues.find((fv) => fv.key === 'object_type')?.value as string;
          return <AttributeNameFilter value={value} onChange={onChange} objectType={objectTypeValue} />;
        },
      },
      {
        key: 'action',
        label: 'Change Type*',
        type: 'custom' as const,
        icon: 'edit' as const,
        selectedLabel: (value: string) => {
          const labels: Record<string, string> = {
            created: 'Created',
            updated: 'Updated',
            deleted: 'Deleted',
          };
          return labels[value] ?? value;
        },
        editComponent: ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
          const objectTypeValue = filterValues.find((fv) => fv.key === 'object_type')?.value as string;
          const hasObjectType = objectTypeValue != null && objectTypeValue !== '';
          return <ActionFilter value={value} onChange={onChange} hasObjectType={hasObjectType} />;
        },
      },
      {
        key: 'start_time',
        label: 'Start Date',
        type: 'custom' as const,
        icon: 'calendar' as const,
        selectedLabel: (value: string) => {
          try {
            const dateStr = value.split('T')[0];
            return `Start: ${dateStr}`;
          } catch {
            return `Start: ${value}`;
          }
        },
        editComponent: ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
          <DateFilter
            value={value}
            onChange={onChange}
            label="Start Date"
            placeholder="Choose start date..."
            timeOfDay="start"
          />
        ),
      },
      {
        key: 'end_time',
        label: 'End Date',
        type: 'custom' as const,
        icon: 'calendar' as const,
        selectedLabel: (value: string) => {
          try {
            const dateStr = value.split('T')[0];
            return `End: ${dateStr}`;
          } catch {
            return `End: ${value}`;
          }
        },
        editComponent: ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
          <DateFilter
            value={value}
            onChange={onChange}
            label="End Date"
            placeholder="Choose end date..."
            timeOfDay="end"
          />
        ),
      },
    ],
    [context, filterValues, allUsers, isLoadingUsers, onLoadUsers],
  );

  // Memoize DataTable configuration objects to prevent unnecessary re-renders
  const sortingConfig = useMemo(
    () => ({
      value: sorting,
      onChange: (newSorting: { id: string; desc: boolean }) => {
        setSorting(newSorting);
        // Update the filter's sort order based on the sorting
        if (newSorting.id === 'timestamp') {
          onFiltersChange({ sort_order: newSorting.desc === true ? 'desc' : 'asc' });
        }
      },
      isManual: true,
    }),
    [sorting, onFiltersChange],
  );

  const filtersConfig = useMemo(
    () => ({
      values: filterValues,
      filters: auditFilters,
      onChange: handleFilterChange,
      onClear: handleFilterClear,
      onClearAll: handleFilterClearAll,
    }),
    [filterValues, auditFilters, handleFilterChange, handleFilterClear, handleFilterClearAll],
  );

  const loadMoreConfig = useMemo(
    () => ({
      canLoad: hasMore,
      onClick: handleLoadMore,
      isLoading: isLoadingMore,
      label: 'Load More',
    }),
    [hasMore, handleLoadMore, isLoadingMore],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('timestamp', {
        id: 'timestamp',
        header: 'Timestamp',
        cell: (info) => {
          const timestamp = info.getValue();
          // Convert Unix timestamp (seconds) to Date
          const date = new Date(timestamp * 1000);

          // Format full timestamp for tooltip
          const fullTimestamp = date.toLocaleString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'long',
          });

          // Get timezone abbreviation
          const timezoneName = date
            .toLocaleString('en-US', {
              timeZoneName: 'short',
            })
            .split(' ')
            .pop();

          const humanFriendlyDate = formatHumanFriendlyDate(date, timezoneName);

          return (
            <Tooltip content={fullTimestamp}>
              <Text size="s" color="subtle">
                {humanFriendlyDate}
              </Text>
            </Tooltip>
          );
        },
        enableSorting: true,
        sortingFn: 'basic',
        meta: {
          width: '200px',
        },
      }),
      columnHelper.accessor('user_id', {
        id: 'user',
        header: 'User',
        cell: (info) => <Text size="s">{getUserEmail(info.getValue())}</Text>,
        enableSorting: false,
        meta: {
          width: '200px',
        },
      }),
      columnHelper.accessor('ip_addr', {
        id: 'ip_address',
        header: 'IP Address',
        cell: (info) => (
          <Text size="s" style={{ fontFamily: 'monospace' }}>
            {info.getValue()}
          </Text>
        ),
        enableSorting: false,
        meta: {
          width: '150px',
        },
      }),
      columnHelper.accessor('object_type', {
        id: 'object_type',
        header: 'Object Type',
        cell: (info) => {
          const displayName = getObjectTypeDisplayName(info.getValue(), context);
          return (
            <Tooltip content={displayName}>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                }}
              >
                {displayName}
              </span>
            </Tooltip>
          );
        },
        enableSorting: false,
        meta: {
          width: '150px',
        },
      }),
      columnHelper.accessor('action', {
        id: 'change_type',
        header: 'Change Type',
        cell: (info) => {
          const action = info.getValue();
          const actionLower = action.toLowerCase();

          let color: 'green' | 'yellow' | 'red' | 'grey' = 'grey';
          if (actionLower === 'created') color = 'green';
          if (actionLower === 'updated') color = 'yellow';
          if (actionLower === 'deleted') color = 'red';

          return (
            <Label color={color} bold>
              {action}
            </Label>
          );
        },
        enableSorting: false,
        meta: {
          width: '120px',
        },
      }),
      columnHelper.accessor('object_id', {
        id: 'object_id',
        header: 'Object ID',
        cell: (info) => (
          <Text size="s" style={{ fontFamily: 'monospace' }}>
            {info.getValue()}
          </Text>
        ),
        enableSorting: false,
        meta: {
          width: '80px',
        },
      }),
      columnHelper.display({
        id: 'changes',
        header: 'Changes',
        cell: (info) => {
          const log = info.row.original;

          // Only show changes for "Updated" actions
          if (log.action.toLowerCase() !== 'updated') {
            return null;
          }

          // Find the previous log entry to compare
          // Use logsRef to access current logs without recreating columns
          const previousLog = logsRef.current.find((l) => {
            // Try version-based lookup first
            if (log.version > 1 && l.version === log.version - 1) {
              return l.object_type === log.object_type && l.object_id === log.object_id;
            }
            // Fallback to timestamp-based lookup
            return (
              l.object_type === log.object_type &&
              l.object_id === log.object_id &&
              l.id !== log.id &&
              (l.timestamp < log.timestamp ||
                (l.timestamp === log.timestamp &&
                  l.action.toLowerCase() === 'created' &&
                  log.action.toLowerCase() === 'updated'))
            );
          });

          if (previousLog == null) {
            return (
              <Text size="s" color="subtle">
                —
              </Text>
            );
          }

          // Compare the data to find changed fields
          const currentData =
            log.data != null && typeof log.data === 'object' ? (log.data as Record<string, unknown>) : {};
          const previousData =
            previousLog.data != null && typeof previousLog.data === 'object'
              ? (previousLog.data as Record<string, unknown>)
              : {};

          // Use the shared flattening logic to get changed fields (respects Campaign/fsm skipping)
          const changedFields: string[] = [];
          const allKeys = new Set([...Object.keys(currentData), ...Object.keys(previousData)]);

          allKeys.forEach((key) => {
            const oldValue = previousData[key];
            const newValue = currentData[key];
            const fieldNames = getChangedFieldNames(oldValue, newValue, key);
            changedFields.push(...fieldNames);
          });

          if (changedFields.length === 0) {
            return (
              <Text size="s" color="subtle">
                None
              </Text>
            );
          }

          // Show first 3 fields + count (format names for display)
          const displayFields = changedFields.slice(0, 3).map(formatAttributeName);
          const remainingFields = changedFields.slice(3).map(formatAttributeName);
          const remainingCount = remainingFields.length;

          return (
            <Box display="flex" flexWrap="wrap" gap="050" alignItems="center">
              {displayFields.map((field) => (
                <span
                  key={field}
                  style={{
                    fontFamily: 'monospace',
                    padding: '2px 6px',
                    backgroundColor: themeVars.color['surface-subtle'],
                    borderRadius: themeVars.radius['3'],
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {field}
                </span>
              ))}
              {remainingCount > 0 ? (
                <Tooltip content={remainingFields.join(', ')}>
                  <span
                    style={{
                      fontSize: '11px',
                      cursor: 'help',
                      textDecoration: 'underline dotted',
                      color: themeVars.color['text-subtle'],
                    }}
                  >
                    +{remainingCount} more
                  </span>
                </Tooltip>
              ) : null}
            </Box>
          );
        },
        meta: {
          width: '300px',
        },
      }),
    ],
    [context, users],
  );

  // Show error banner but keep the UI intact so users can clear filters
  const [isDismissed, setIsDismissed] = useState(false);

  // Reset dismissed state when error changes
  useEffect(() => {
    if (error != null) {
      setIsDismissed(false);
    }
  }, [error]);

  const errorBanner = error != null && !isDismissed && (
    <Banner
      variant="error"
      title="Error loading audit logs"
      shouldShowDismissButton
      onClose={() => setIsDismissed(true)}
      action={{
        label: 'Clear filters',
        onClick: () => {
          handleFilterClearAll();
          setIsDismissed(true);
        },
      }}
    >
      <Text mt="200">
        {error instanceof Error ? error.message : 'An unexpected error occurred'}
        {'. '}Try adjusting your filters or clearing them to see results.
      </Text>
    </Banner>
  );

  // ExpandedRowComponent: Wrapper around AuditLogExpandedDetails for use within DataTable
  // The DataTable's expandedRowComponent receives { row: { original: AuditLogEntry } }
  // We extract the log and pass it along with context to the shared component
  // Memoized with useCallback to prevent unnecessary re-renders of expanded rows
  // Uses memoizedLogs to avoid recreating component when logs array reference changes but contents are the same
  const ExpandedRowComponent = useCallback(
    ({ row }: { row: { original: AuditLogEntry } }) => {
      const log = row.original as AuditLogEntry;
      return <AuditLogExpandedDetails log={log} context={context} contextId={contextId} logs={memoizedLogs} />;
    },
    [context, contextId, memoizedLogs],
  );

  // Custom empty state component
  const EmptyState = () => (
    <Box p="800" textAlign="center">
      <Text>No audit logs found matching your criteria.</Text>
    </Box>
  );

  return (
    <Stack gap="400" mb="800">
      {/* Error Banner */}
      {errorBanner}

      {/* Summary */}
      {logs.length > 0 && isLoading === false && error == null ? (
        <Box>
          <Text size="s">
            Showing {logs.length} audit log{logs.length === 1 ? '' : 's'}
          </Text>
        </Box>
      ) : null}

      {/* DataTable */}
      <DataTable
        caption="Audit log entries"
        data={error != null ? [] : logs}
        columns={columns}
        getRowId={(row) => row.id.toString()}
        withRowExpanding={true}
        expandedRowComponent={ExpandedRowComponent}
        getRowCanExpand={() => true}
        initiallyExpandedRows={highlightedLogId != null && highlightedLogId !== '' ? { [highlightedLogId]: true } : {}}
        isLoading={isLoading === true && error == null}
        loadingState={{ rowCount: 5 }}
        emptyStateComponent={EmptyState}
        layout="fixed"
        withSorting={true}
        sorting={sortingConfig}
        withFilters={true}
        filters={filtersConfig}
        loadMore={error != null ? undefined : loadMoreConfig}
        withColumnsSettings={true}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />
      {!hasMore && logs.length > 0 && isDeepLinkMode === false && error == null ? (
        <Box textAlign="center" p="400">
          <Text size="s" color="subtle">
            All audit logs loaded ({logs.length} total)
          </Text>
        </Box>
      ) : null}
    </Stack>
  );
};
